// Cyborg7 Connect — the client's view of "who in this room is from another company".
//
// TWO KINDS OF EXTERNAL, AND THEY MUST NOT COLLAPSE (spec §1.2):
//
//   bridge guest   pseudo-user, id `slack:<team>:<user>` / `teams:…`, no account, no
//                  profile, display-only. Keeps ExternalGuestBadge + isExternalGuest
//                  (utils.ts) — a pill with the PROVIDER'S mark answering "which pipe
//                  did this come through".
//   Connect external  a real Cyborg account with a profile, presence and DMs, belonging
//                  to another organization. Its marker names their ORGANIZATION and
//                  answers "which company are they from". No provider mark, no pipe.
//
// isExternalGuest() is a string-prefix check on the id and MUST keep firing only for the
// bridge kind. A Connect external's id is an ordinary uuid, so it never matches — that is
// deliberate, not an oversight: widening the prefix check to cover Connect would make a
// partner's message read like a forwarded Slack post. connectOrgFor() below re-asserts the
// separation with a guard so the two can never cross even if a payload is seeded wrong.
//
// TODO(connect-data): every field here is fed by `hydrate()` from a stub. The relay RPCs
// live on the `connect-data` branch; the shapes this file ASSUMES are spelled out in
// "SERVER SHAPES ASSUMED" at the bottom. Reconcile at merge — do not guess a second time.
import { untrack } from "svelte";
import { isExternalGuest } from "$lib/utils.js";
import { workspaceState } from "$lib/core/state.svelte.js";
import { client } from "./client.js";

/** A partner organization. `name` is what the avatar mark and every banner say. */
export interface ConnectOrg {
  id: string;
  name: string;
}

/** Mirrors the connection state machine in spec §4 ("Conexión" + its overlays). */
export type ConnectionStatus = "pending" | "connected" | "disconnected" | "blocked" | "frozen";

/**
 * ONE PERSON'S WAY IN — who admitted them, when, and into which channel.
 *
 * A connection is per-ORGANIZATION and an invitation is per-person and per-channel, so a
 * row carries several of these and they need not name the same inviter. That is the whole
 * reason it is a list: "who let Nova in" can honestly be four different people, and a
 * screen that showed one of them would be naming an inviter the company never had.
 */
export interface ConnectInvite {
  /** The person let in. Their ADDRESS — never null. */
  email: string;
  /**
   * Who sent it: their name, else their address. NULL when the host account that
   * invited them is gone (`invited_by` is a SET NULL foreign key), and the screen renders
   * that as an absence. There is no fallback: the owner, the oldest admin or a blank
   * would each be a name we made up on the one screen an admin comes to in order to
   * CHECK.
   */
  invitedByName: string | null;
  /**
   * epoch ms — when this person's connection row was opened, i.e. the first invitation.
   * A re-invite re-stamps the inviter and leaves this alone, so the name and the date are
   * two facts about one person, not one event, and the screen never says "X invited them
   * ON that date".
   */
  invitedAt: number;
  /**
   * The channel that invitation opened, null when it has since been deleted. The INVITE's
   * channel only: nothing records who added somebody to a second channel, so this is the
   * only "who let them into #x" there is an answer to.
   */
  channelId: string | null;
}

export interface ConnectConnection {
  org: ConnectOrg;
  status: ConnectionStatus;
  /** epoch ms; null while the invitation is still pending. */
  connectedAt: number | null;
  /** People from that org who are in at least one of our channels. */
  peopleCount: number;
  /** Channels we share with that org. */
  channelCount: number;
  /**
   * The invitee's ADDRESS, on a row that has no organization yet.
   *
   * `home_workspace_id` is stamped on ACCEPT, so a pending invitation has no company
   * name and the server names its row by the mail domain — a guess presented as a
   * fact. An address is the thing we actually know, so the row renders that instead
   * and says the organization is still unknown. Null (or absent, on a relay that does
   * not send it yet) on every accepted connection, where `org.name` is the real name.
   */
  email?: string | null;
  /**
   * How each of that company's people got in, oldest first. OPTIONAL for the same reason
   * `email` is: a relay that has not shipped this yet sends nothing at all. Absent and
   * empty are rendered the same way — the block does not appear — because silence claims
   * nothing, while a heading over no lines would claim nobody invited anybody.
   */
  invites?: ConnectInvite[];
}

/** One payload = one full replacement of the client's Connect view. */
export interface ConnectSnapshot {
  /**
   * The Connections screen's rows. EMPTY when *I* am the external: who else this host
   * shares channels with, who invited whom and what it costs is their business.
   */
  connections: ConnectConnection[];
  /**
   * Every org I may see a mark for, id → name. SEPARATE from `connections` because the
   * two have different audiences: an external gets no connection rows at all but still
   * has to be able to name the company on somebody's avatar, and a host member's own
   * name resolution must not depend on a list that is empty for half the viewers.
   */
  orgs: ConnectOrg[];
  /** userId → orgId, for every Connect external I can see. */
  orgByUser: Record<string, string>;
  /** channelId → orgId[], the orgs present in each shared channel. */
  orgsByChannel: Record<string, string[]>;
  /**
   * Set when *I* am the external in the workspace I'm looking at; null when I'm a host
   * member. Drives every "hide what an external must not see" branch in the shell.
   */
  viewerOrgId: string | null;
}

/**
 * The invitee-side preview of an invitation (spec §8.2). No UI renders it any more.
 *
 * FOUR REFUSALS, NOT ONE "invalid invitation". They are different facts and they lead to
 * different actions: an expired one can be re-sent, a revoked one cannot, an accepted one
 * is not a failure at all, and "none" is almost always the wrong account signed in. The
 * server derives `state` (connect-external.ts's `externalInviteState`) so the clock is
 * read in exactly one place.
 */
export type ExternalInviteState = "invited" | "expired" | "active" | "revoked" | "none";

export interface ExternalInvitePreview {
  state: ExternalInviteState;
  /** The HOST organization. Null only when `state` is "none" — no row, nothing to name. */
  workspace: { id: string; name: string } | null;
  /**
   * The one channel this invitation opens. Null when it was deleted between the invite
   * and now, which is legal: the connection is still theirs, they just land nowhere.
   */
  channel: { id: string; name: string } | null;
  /** Who sent it, for the "X at Acme invited you" line. Their name, else their address. */
  invitedByName: string | null;
  /** epoch ms; null once accepted or revoked — a live connection has no deadline. */
  expiresAt: number | null;
}

/** Mail hosts that are a person, not a company — a domain match there names nobody. */
const CONSUMER_EMAIL_DOMAINS = new Set([
  "gmail.com",
  "googlemail.com",
  "outlook.com",
  "hotmail.com",
  "live.com",
  "msn.com",
  "yahoo.com",
  "ymail.com",
  "icloud.com",
  "me.com",
  "mac.com",
  "proton.me",
  "protonmail.com",
  "pm.me",
  "aol.com",
  "gmx.com",
  "mail.com",
  "zoho.com",
  "yandex.com",
  "fastmail.com",
  "hey.com",
  "duck.com",
]);

/**
 * Provisional organization name for an email address, for step 1 of the invite ("detected
 * org"). Domain minus its public suffix, title-cased: `dana@acme-corp.co.uk` → "Acme Corp".
 *
 * Returns null for a consumer mailbox and for anything that isn't a plausible address —
 * naming an organization we cannot name is exactly the kind of confident lie this screen
 * must not tell. The AUTHORITATIVE name is the invitee's real workspace name, resolved
 * server-side. TODO(connect-data): replace the rendered value with that once the lookup
 * exists; this stays as the pre-response placeholder.
 */
export function orgNameFromEmail(email: string): string | null {
  const at = email.trim().toLowerCase().lastIndexOf("@");
  if (at < 1) return null;
  const domain = email
    .trim()
    .toLowerCase()
    .slice(at + 1);
  if (!domain.includes(".") || domain.endsWith(".") || domain.startsWith(".")) return null;
  if (CONSUMER_EMAIL_DOMAINS.has(domain)) return null;
  // Drop the public suffix. Two labels for the common ccTLD-under-gTLD shapes
  // (.co.uk, .com.ar, .com.au…), one otherwise. ponytail: a static suffix list, not
  // the PSL — swap in the PSL only if a real invite is mis-labelled.
  const labels = domain.split(".");
  const secondLast = labels.at(-2);
  const drop = labels.length > 2 && secondLast != null && secondLast.length <= 3 ? 2 : 1;
  const stem = labels.slice(0, Math.max(1, labels.length - drop)).join(" ");
  const words = stem
    .split(/[-_. ]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1));
  return words.length > 0 ? words.join(" ") : null;
}

class ConnectState {
  /**
   * The workspace this view belongs to, remembered by load(). Every Connect RPC is
   * workspace-scoped on the relay, and the surfaces that reach this store hold a
   * channel or an org id but not always the workspace — so it lives here once
   * instead of being threaded through five component signatures.
   */
  workspaceId = $state<string | null>(null);
  connections = $state<ConnectConnection[]>([]);
  orgs = $state<ConnectOrg[]>([]);
  orgByUser = $state<Record<string, string>>({});
  orgsByChannel = $state<Record<string, string[]>>({});
  viewerOrgId = $state<string | null>(null);

  /**
   * `workspaceId`, read WITHOUT subscribing the caller to it. Every command below reads
   * it and then writes it, or writes the rest of the store off it.
   *
   * THE REASON IT EXISTS. load() is called from $effects — the workspace layout's
   * (routes/workspace/[id]/+layout.svelte) and the Connections screen's own. Inside one,
   * a TRACKED read makes that effect a dependent of the very field the next lines write,
   * so the effect invalidates itself: `effect_update_depth_exceeded`, the page never
   * settles, and the directory the relay already answered never paints. Measured on the
   * live app — a screen reading "No connections yet" over a payload that was on the wire.
   *
   * Here rather than at the two call sites deliberately. A rule the caller has to
   * remember ("wrap this store call in untrack") is the same defect one screen later;
   * a store whose commands do not leak dependencies is safe for the caller nobody has
   * written yet. The GETTERS above stay tracked — orgFor/orgsInChannel/isExternalViewer
   * are what components render from, and they must re-run when a payload lands.
   */
  private get loadedWorkspaceId(): string | null {
    return untrack(() => this.workspaceId);
  }

  /** True when the signed-in person is a Connect external in the workspace on screen. */
  get isExternalViewer(): boolean {
    return this.viewerOrgId !== null;
  }

  /** My own organization, when I am the external here. */
  get viewerOrg(): ConnectOrg | null {
    return this.viewerOrgId ? this.orgById(this.viewerOrgId) : null;
  }

  orgById(orgId: string): ConnectOrg | null {
    return this.orgs.find((o) => o.id === orgId) ?? null;
  }

  /** The organization a person belongs to, or null for a host member. */
  orgFor(userId: string): ConnectOrg | null {
    const orgId = this.orgByUser[userId];
    return orgId ? this.orgById(orgId) : null;
  }

  /** Every partner organization present in a channel. Empty for an internal channel. */
  orgsInChannel(channelId: string): ConnectOrg[] {
    const ids = this.orgsByChannel[channelId];
    if (!ids?.length) return [];
    return ids.map((id) => this.orgById(id)).filter((o): o is ConnectOrg => o !== null);
  }

  /** Replace the whole view. The single ingestion point for the relay payload. */
  hydrate(snapshot: ConnectSnapshot): void {
    this.connections = snapshot.connections;
    this.orgs = snapshot.orgs;
    this.orgByUser = snapshot.orgByUser;
    this.orgsByChannel = snapshot.orgsByChannel;
    this.viewerOrgId = snapshot.viewerOrgId;
  }

  /** Drop everything — on sign-out and on every workspace switch. */
  reset(): void {
    this.workspaceId = null;
    this.connections = [];
    this.orgs = [];
    this.orgByUser = {};
    this.orgsByChannel = {};
    this.viewerOrgId = null;
  }

  /**
   * `cyborg:connect_directory { hostWorkspaceId }` → the whole view, in ONE round trip.
   *
   * A SECOND CALL, not a widening of `cyborg:list_external_connections`, and that was
   * the decision: the connection list is the HOST's (every partner, every person, who
   * invited them) and is fenced off by the relay's membership floor, while this payload
   * has to reach the external too — the mark, the strip and every "hide the host's
   * surfaces" gate are theirs to render. One endpoint serving both would be one endpoint
   * with two contracts, and one careless edit from handing a partner the host's list.
   *
   * ONE call, on workspace entry, not one per channel: the mark has to be on the first
   * paint of any channel, and a per-channel fetch arrives after the avatars do.
   *
   * `hostWorkspaceId` rather than `workspaceId` because the relay's universal floor
   * rejects a `workspaceId` the caller is not scoped to, and an external is deliberately
   * not scoped to the host workspace. The endpoint states its own gate instead.
   *
   * On failure it stays reset — no marks, no strip, no pill. That is the same shell a
   * workspace with no connections renders, and every host surface an external must not
   * reach is separately refused by the server, so a failed load costs a missing marker,
   * never data.
   */
  async load(workspaceId: string): Promise<void> {
    // Clear ONLY on a workspace switch. load() is also the re-read after a membership
    // change in the workspace already on screen (app.svelte.ts's channel_member_added /
    // channel_member_removed), and blanking every mark for the length of a round trip
    // is the worse lie of the two: a marker one beat stale still names the right
    // company, a marker that blinks off says the partner is one of us. The switch still
    // resets, for the reason it always did — the previous org's marks must never paint
    // on the next workspace's avatars.
    //
    // Through `loadedWorkspaceId`, never `this.workspaceId` — see the getter. This
    // comparison is the read that made both calling $effects self-invalidating; a
    // tracked one here is the whole bug, not a style preference.
    if (this.loadedWorkspaceId !== workspaceId) this.reset();
    this.workspaceId = workspaceId;
    try {
      const snapshot = await client.connectDirectory(workspaceId);
      // A workspace switch that landed while this was in flight owns the state now.
      if (this.loadedWorkspaceId !== workspaceId) return;
      this.hydrate(snapshot);
    } catch (err) {
      console.error("Failed to load the Connect directory:", err);
    }
  }
}

export const connectState = new ConnectState();

/**
 * The settings tabs a Connect external keeps in a HOST workspace: their own account and
 * their own device preferences. Everything else on that page — the workspace, its people,
 * its bill, its daemons, its providers, its logs — belongs to the host and is none of a
 * guest's business (spec §3, "Ajustes").
 *
 * An ALLOW-list, not a deny-list, and that is the point: a settings tab added tomorrow is
 * hidden from externals by default, exactly like the 156 membership checks that fail
 * closed because externals were kept out of `memberships` (spec §1.1). A deny-list would
 * leak every future tab silently.
 */
const EXTERNAL_SETTINGS_TABS = new Set(["general", "notifications", "appearance", "about"]);

/** Whether a settings tab is visible to the person on screen. */
export function settingsTabVisible(tabId: string): boolean {
  return !connectState.isExternalViewer || EXTERNAL_SETTINGS_TABS.has(tabId);
}

/**
 * THE derivation every surface calls: which organization is this person from, or null.
 * The counterpart to utils.ts `externalGuestProvider` and deliberately separate from it —
 * one answers "which pipe", this answers "which company" (spec §1.2).
 *
 * The isExternalGuest guard is the fence between the two kinds. A bridge guest can never
 * carry an org mark no matter what a payload claims, so the badge and the mark can never
 * both appear on one avatar.
 */
export function connectOrgFor(userId: string): ConnectOrg | null {
  if (!userId || isExternalGuest(userId)) return null;
  const org = connectState.orgFor(userId);
  // My own colleagues carry no mark: the mark answers "from another company".
  return org && org.id !== connectState.viewerOrgId ? org : null; // null: a host member or my own org
}

/**
 * The OTHER side of a channel, named from where I sit: never my own org, and when I am the
 * outsider (in the host's workspace, or its channel opened in place) the host org first. The
 * header pill and the composer strip both read this.
 */
export function channelPartnerOrgs(channelId: string): ConnectOrg[] {
  const others = connectState
    .orgsInChannel(channelId)
    .filter((o) => o.id !== connectState.viewerOrgId);
  const hostId = workspaceState.channelWorkspaceId(channelId);
  const outsider = hostId !== workspaceState.current?.id || connectState.isExternalViewer;
  const host = outsider ? workspaceState.list.find((w) => w.id === hostId) : undefined;
  return host ? [{ id: host.id, name: host.external?.orgName ?? host.name }, ...others] : others;
}

// ─── SERVER SHAPES (reconcile with `connect-data` at merge) ──────────────────────────
//
// ALREADY SHIPPED on `connect-data` (spec §6 step 3) — this client must use these names:
//   cyborg:list_external_connections   → the HOST's raw connection rows. NOT called from
//                                        this client: see cyborg:connect_directory below,
//                                        which is the second call this file's load() asked
//                                        the server branch to decide on.
//   cyborg:invite_external             ← no UI caller (the invite UI was removed)
//   cyborg:revoke_external             ← the per-PERSON revoke, used by a channel's
//                                        Members tab. Not what the Connections screen
//                                        calls: see cyborg:revoke_external_org below.
//   cyborg:accept_external_invite      ← the invitee's side, not a host surface
//   pg.isExternalCaller(wsId, userId)  ← the server truth behind `viewerOrgId`
//
// SHIPPED ON `connect2-rpc`:
//   cyborg:channel_history_size { workspaceId, channelId }
//     → { messages, files, oldestAt: number | null }
//     The assumed shape was short by one field. A size is not a decision — "1,284
//     messages and 37 files" tells you how much, "since 4 March" tells you how far back,
//     and it is the second half that makes the interstitial a choice rather than a
//     statistic. Members only: it names `workspaceId`, so the relay's universal
//     membership floor keeps an external out, which is right — the audience is the host
//     deciding whether to hand over their own history.
//   cyborg:connect_directory { hostWorkspaceId }
//     → { connections, orgs, orgByUser, orgsByChannel, viewerOrgId }
//     A member OR an ACTIVE external, and the ONE endpoint on this branch an outsider
//     reaches — hence `hostWorkspaceId`, which skips the universal membership floor (an
//     external is deliberately absent from guest.workspaceIds) in favour of its own,
//     stricter gate. An external's payload is narrowed to the people they share a channel
//     with and the channels they sit in, and carries no `connections` at all.
//     `orgs` was ADDED to the assumed shape: an external gets no connection rows but
//     still has to name the company on an avatar, so name resolution cannot hang off a
//     list that is empty for half the viewers.
//
// ASKED FOR BY THIS BRANCH, NOT BUILT YET (the Connections console, CYBORG-963). The
// client calls both today and treats a rejection as a rejection, so the screen is honest
// on a relay that has neither:
//   cyborg:revoke_external_org { workspaceId, orgId }
//     → ends the connection with ONE ORGANIZATION: every `workspace_externals` row of
//       that org in this workspace flips to 'revoked' AND every `channel_members` seat
//       those people hold in this workspace goes with it, in ONE transaction, and the
//       live sockets are re-scoped the way accept_external_invite re-scopes them.
//       `orgId` is the id the connection row carries — the partner's own workspace id,
//       or `external:<userId>` for an invitation nobody accepted yet (that shape is
//       minted by connect-directory.ts's orgIdFor, so the relay resolves it back to
//       the one user). Owner/admin only. REJECTS on refusal; an empty resolve is read
//       as success by the screen.
//   cyborg:connect_directory's `connections[]` needs ONE more field: `email`, the
//     invitee's address, on every row whose org is not yet known (`home_workspace_id`
//     null). Today those rows are named by the mail domain — `orgNameFor` falls back to
//     `row.email.slice(at + 1)` — which is a company name we invented from an address.
//     The address is the fact we hold; the org name only exists once they accept.
//
// SHIPPED WITH THE CONNECTIONS CONSOLE (this branch):
//   cyborg:connect_directory's `connections[]` carries `invites[]` — one entry per LIVE
//     person of that organization: `{ email, invitedByName, invitedAt, channelId }`, read
//     straight off `workspace_externals` (`invited_by` resolved to a name server-side,
//     `created_at`, `invite_channel_id`). It answers the second question an admin asks
//     after "which channels": who let them in, and when. Inside `connections[]` rather
//     than beside it so it inherits that array's one fence — empty for an external.
//     WHICH CHANNELS needed no new RPC and no new field: `orgsByChannel` already carries
//     it, and the workspace's channels are already in `workspaceState.channels`.
//
// NOT assumed and NOT called anywhere in the UI: any permission/tier/capability field.
// There are none by decision (spec §1.4) — if the server grows one, the UI is wrong, not
// the spec.

/** One-shot request for ChannelSidebar to open its own create-channel form. */
export const freshChannelRequest = $state({ pending: false });
